import { Interface, getAddress } from 'ethers'
import type { Hex } from '../types.js'

/**
 * Browser-side selector decoder backed by OpenChain's signature database
 * (https://api.openchain.xyz/signature-database/v1/lookup). It's the same
 * source `cast 4byte-decode` queries — no server needed, CORS works fine,
 * and we cache hits in localStorage so re-decoding the same selector is
 * instant.
 *
 * `filter=true` drops spam entries that the registry sometimes accumulates
 * for popular 4-byte selectors. We try signatures in returned order and
 * pick the first one that actually decodes the calldata cleanly.
 */

const CACHE_KEY = 'safeward.fourbyte.cache.v1'
const NEGATIVE_TTL_MS = 5 * 60_000

interface CacheEntry {
  sigs: string[]
  /** Unix ms when this entry was written. Used for negative cache TTL. */
  writtenAt: number
}

type Cache = Record<string, CacheEntry>

let mem: Cache | null = null

function load(): Cache {
  if (mem) return mem
  try {
    mem = JSON.parse(localStorage.getItem(CACHE_KEY) ?? '{}') as Cache
  } catch {
    mem = {}
  }
  return mem
}

function save(): void {
  if (!mem) return
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(mem))
  } catch {
    // localStorage full / disabled — non-fatal
  }
}

async function lookupSelector(selector: string): Promise<string[]> {
  const sel = selector.toLowerCase()
  const cache = load()
  const hit = cache[sel]
  if (hit) {
    // Negative results expire so a temporarily-down API doesn't lock us out
    if (hit.sigs.length > 0) return hit.sigs
    if (Date.now() - hit.writtenAt < NEGATIVE_TTL_MS) return hit.sigs
  }

  try {
    const url = `https://api.openchain.xyz/signature-database/v1/lookup?function=${sel}&filter=true`
    const resp = await fetch(url)
    if (!resp.ok) throw new Error(`openchain ${resp.status}`)
    const json = (await resp.json()) as {
      ok: boolean
      result?: { function?: Record<string, { name: string }[]> }
    }
    const entries = json.result?.function?.[sel] ?? []
    const sigs = entries.map((e) => e.name)
    cache[sel] = { sigs, writtenAt: Date.now() }
    save()
    return sigs
  } catch {
    cache[sel] = { sigs: [], writtenAt: Date.now() }
    save()
    return []
  }
}

export interface DecodedArg {
  name: string | null
  type: string
  /** Pretty printed for UI. Original raw value also kept in `raw`. */
  value: string
  raw: unknown
}

export interface DecodedCall {
  /** Canonical signature, e.g. "withdraw(uint256,address,address)". */
  signature: string
  /** Function name only, e.g. "withdraw". */
  name: string
  args: DecodedArg[]
}

export async function decodeWithFourbyte(
  data: Hex | string,
): Promise<DecodedCall | null> {
  if (!data || !data.startsWith('0x') || data.length < 10) return null
  const selector = data.slice(0, 10).toLowerCase()
  const sigs = await lookupSelector(selector)
  // Prefer signatures without `_unknown` or trailing junk — those tend to be
  // spam decompilations. Real ABIs use plain identifiers.
  const ranked = [...sigs].sort((a, b) => {
    const a_score = (a.match(/_/g)?.length ?? 0) * 10 + a.length
    const b_score = (b.match(/_/g)?.length ?? 0) * 10 + b.length
    return a_score - b_score
  })

  for (const sig of ranked) {
    try {
      const iface = new Interface([`function ${sig}`])
      const fn = iface.fragments[0]
      if (!fn || fn.type !== 'function') continue
      const parsed = iface.parseTransaction({ data })
      if (!parsed) continue
      const args: DecodedArg[] = parsed.args.map((value, i) => {
        const input = parsed.fragment.inputs[i]
        return {
          name: input?.name || null,
          type: input?.type || 'unknown',
          value: formatValue(value, input?.type),
          raw: value,
        }
      })
      return { signature: sig, name: parsed.name, args }
    } catch {
      // Try the next candidate
    }
  }
  return null
}

function formatValue(v: unknown, type?: string): string {
  if (v === null || v === undefined) return 'null'
  if (typeof v === 'bigint') {
    return formatBigInt(v, type)
  }
  if (typeof v === 'string') {
    if (type === 'address') {
      try {
        return getAddress(v)
      } catch {
        return v
      }
    }
    return v
  }
  if (Array.isArray(v)) {
    return `[${v.map((x) => formatValue(x)).join(', ')}]`
  }
  return String(v)
}

function formatBigInt(v: bigint, type?: string): string {
  // Heuristic: massive uint256s are usually token amounts; suffix with the
  // raw decimal but also note "looks like UNLIMITED" for max uint256.
  if (v === 2n ** 256n - 1n) return 'UNLIMITED (2^256-1)'
  // Keep raw decimal; the UI doesn't know the token's decimals here so we
  // can't pretty-print "10 USDC" reliably. Showing the integer is honest.
  if (type === 'uint256' || type === 'int256' || !type) {
    if (v > 10n ** 15n) {
      // Likely fixed-point; show with 18 dec assumption alongside raw
      return `${v} (~${formatFixed(v, 18)} @ 18d)`
    }
  }
  return v.toString()
}

function formatFixed(v: bigint, decimals: number): string {
  const divisor = 10n ** BigInt(decimals)
  const whole = v / divisor
  const frac = v % divisor
  if (frac === 0n) return whole.toString()
  const fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '')
  return `${whole}.${fracStr.slice(0, 6)}`
}
